import { buildAccept, type FileDropConfig, type FileRejection, getErrorMessage } from '@magicschool/spellbook/components/Dropzone';
import { type RefObject, useCallback, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';

interface UseFileDropOptions {
  fileDrop: FileDropConfig | undefined;
  disabled: boolean | undefined;
  isDragActiveProp: boolean | undefined;
  isDragAcceptProp: boolean | undefined;
  isDragRejectProp: boolean | undefined;
  containerRef: RefObject<HTMLDivElement | null>;
}

export function useFileDrop({
  fileDrop,
  disabled,
  isDragActiveProp,
  isDragAcceptProp,
  isDragRejectProp,
  containerRef,
}: UseFileDropOptions) {
  const [dropRejections, setDropRejections] = useState<FileRejection[]>([]);
  const [statusMessage, setStatusMessage] = useState<string>('');

  const clearDropError = useCallback(() => setDropRejections([]), []);

  const dropzoneAccept = useMemo(() => (fileDrop?.accept ? buildAccept(fileDrop.accept) : undefined), [fileDrop?.accept]);
  const onDropProp = fileDrop?.onDrop;
  const dropText = fileDrop?.text;
  const dropMaxSize = fileDrop?.maxSize;
  const dropDisabled = disabled || fileDrop?.disabled;

  const handleDrop = useCallback(
    (accepted: File[], rejections: FileRejection[]) => {
      setDropRejections(rejections);

      const announcements = dropText?.announcements;
      const firstRejection = rejections[0];
      const rejectionMessage = firstRejection ? getErrorMessage(firstRejection, dropText, { maxSize: dropMaxSize }) : '';

      if (accepted.length > 0 && rejections.length === 0) {
        setStatusMessage(announcements?.added?.(accepted.length) ?? `${accepted.length} file${accepted.length === 1 ? '' : 's'} added.`);
      } else if (accepted.length === 0 && rejections.length > 0) {
        setStatusMessage(
          announcements?.rejected?.(rejections.length, rejectionMessage) ??
            `${rejections.length} file${rejections.length === 1 ? '' : 's'} rejected. ${rejectionMessage}`,
        );
      } else if (accepted.length > 0 && rejections.length > 0) {
        setStatusMessage(
          announcements?.mixed?.(accepted.length, rejections.length, rejectionMessage) ??
            `${accepted.length} added, ${rejections.length} rejected. ${rejectionMessage}`,
        );
      }

      if (accepted.length > 0) {
        onDropProp?.(accepted, rejections);
      }
      // rAF lets React commit before focus shifts — fires for both accepted and all-rejected drops.
      requestAnimationFrame(() => {
        containerRef.current?.querySelector<HTMLTextAreaElement>('textarea')?.focus({ preventScroll: true });
      });
    },
    [onDropProp, dropText, dropMaxSize, containerRef],
  );

  const handleDragEnter = useCallback(() => {
    setDropRejections([]);
    setStatusMessage('');
  }, []);

  const {
    getRootProps: getDropRootProps,
    rootRef: dropzoneRootRef,
    isDragActive: isDragActiveInternal,
    isDragAccept: isDragAcceptInternal,
    isDragReject: isDragRejectInternal,
  } = useDropzone({
    accept: dropzoneAccept,
    maxSize: fileDrop?.maxSize,
    maxFiles: fileDrop?.maxFiles,
    multiple: fileDrop?.multiple ?? true,
    validator: fileDrop?.validator,
    disabled: !fileDrop || dropDisabled,
    noClick: true,
    noKeyboard: true,
    onDrop: handleDrop,
    onDragEnter: handleDragEnter,
  });

  // Prop overrides take precedence — same pattern as Dropzone — so Storybook/tests can pin drag-state.
  const isDragActive = isDragActiveProp ?? isDragActiveInternal;
  const isDragAccept = isDragAcceptProp ?? isDragAcceptInternal;
  const isDragReject = isDragRejectProp ?? isDragRejectInternal;

  const dropError = useMemo(() => {
    const firstRejection = dropRejections[0];
    if (!firstRejection) return null;
    const firstMessage = getErrorMessage(firstRejection, dropText, { maxSize: dropMaxSize });
    if (dropRejections.length > 1 && dropText?.multipleRejected) {
      return dropText.multipleRejected(dropRejections.length, firstMessage);
    }
    return firstMessage;
  }, [dropRejections, dropText, dropMaxSize]);

  // Kept separate from statusMessage so the live region switches between drag and drop announcements cleanly.
  const dragAnnouncement = useMemo(() => {
    if (!isDragActive) return null;
    const announcements = dropText?.announcements;
    if (isDragReject) return announcements?.dragReject ?? 'File not accepted.';
    if (isDragAccept) return announcements?.dragAccept ?? 'Release to attach.';
    return null;
  }, [isDragActive, isDragAccept, isDragReject, dropText]);

  return {
    getDropRootProps,
    dropzoneRootRef,
    isDragActive,
    isDragAccept,
    isDragReject,
    dropError,
    clearDropError,
    dragAnnouncement,
    statusMessage,
  };
}
